/**
 * The stages one accelerator application moves through in its canvas block
 * (flag `accelerators-row`): Draft, Evaluate, Expand, Submit, Interview prep.
 * Read by the Application lightbox, the closed Application block and the
 * Accelerator Apps panel's block chrome, so all three name the same stage.
 *
 * Stages are derived from the application's state, never stored. Which stage
 * the founder is looking at is view state; the default follows the work.
 *
 * Cross-compatible (no "use client"), pure.
 */
import { ACCELERATOR_PHASES } from "@/constants/acceleratorsRow";
import type { ApplicationQuestion } from "@/constants/accelerators/questions/types";
import type { AppFormatId } from "@/constants/accelerators/applications";
import { PROGRAM_APPLICATIONS } from "@/constants/programApplications";
import type { ProgramOffers, ScriptedVideo } from "@/constants/programs";
import type { AcceleratorRowApplication } from "@/lib/accelerators/deriveRow";

export const APPLICATION_STAGES = {
  DRAFT: "draft",
  IMPROVE: "improve",
  /** Think Bigger: no application question asks for it, so it sits after Evaluate and is never scored. */
  EXPAND: "expand",
  SUBMIT: "submit",
  INTERVIEW: "interview",
} as const;

export type ApplicationStage = (typeof APPLICATION_STAGES)[keyof typeof APPLICATION_STAGES];

/** The stages that carry Coach my answer: every stage that shows the founder's answers (LEV-2632). */
export type CoachStage = Extract<
  ApplicationStage,
  typeof APPLICATION_STAGES.DRAFT | typeof APPLICATION_STAGES.IMPROVE | typeof APPLICATION_STAGES.SUBMIT
>;

export const APPLICATION_STAGE_LABELS: Record<ApplicationStage, string> = {
  [APPLICATION_STAGES.DRAFT]: "Draft",
  [APPLICATION_STAGES.IMPROVE]: "Evaluate",
  [APPLICATION_STAGES.EXPAND]: "Think bigger",
  [APPLICATION_STAGES.SUBMIT]: "Submit",
  [APPLICATION_STAGES.INTERVIEW]: "Interview prep",
};

/** What the Accelerator Apps panel can see of the work. */
export interface ApplicationWork {
  /** A drafted version exists. */
  hasDraft: boolean;
  /** Lev is drafting or scoring. */
  generating: boolean;
}

/** What the panel reports up: the work, whether its last draft failed, and whether it is scored. */
export interface ApplicationPanelReport extends ApplicationWork {
  /**
   * The last draft ended in error. A failed draft never restarts by itself:
   * only the panel's retry button starts it again.
   */
  failed: boolean;
  /** Lev's scores exist. Only the panel knows; the row never carries them. */
  scored: boolean;
  /** Null for a format with no spec or no draft. */
  answers?: DraftedAnswerCount | null;
  /**
   * The server would run interview prep for this application (getApp, by
   * the owner's flags); undefined until the panel has a row to read it from.
   */
  interviewPrepAvailable?: boolean;
}

/** Scored answers only: a basic (completeness field) is captured on Submit and never counted here. */
export interface DraftedAnswerCount {
  drafted: number;
  total: number;
}

export interface ApplicationProgress extends ApplicationWork {
  /** The founder submitted it. Only the row knows this; the panel never fetches it. */
  submitted: boolean;
  /**
   * Opens on Draft even over a draft, until submitted: the founder came in
   * through this program's lander, and Lev scores every draft right after
   * writing it, so they would otherwise land on a score first (LEV-2688).
   * Nothing records a submission yet (`submittedAt` has no column), so the
   * hold never releases today; it does once that marker lands (LEV-2239).
   * Absent means false.
   */
  holdsOnDraft?: boolean;
}

/**
 * How long Draft holds the first draft for a Big Idea that has not landed before
 * it offers to draft without it. A Big Idea that failed on the canvas would
 * otherwise hold the founder here forever.
 */
export const BIG_IDEA_WAIT_FALLBACK_MS = 120_000;

/**
 * Whether the format's evaluation carries interview questions, and so has an
 * Interview prep stage. evaluateApp asks for them only where this is true.
 */
export function formatHasInterviewPrep(formatId: AppFormatId): boolean {
  const spec = PROGRAM_APPLICATIONS[formatId];
  return spec?.tier === "premier" && spec.interviewPrep !== null;
}

/**
 * The question data a format's draft splits into answers, or null for a
 * format with no spec, whose draft is one free-form document. Non-null picks
 * the per-answer view, the answer count and the Submit checklist.
 */
export function formatQuestions(formatId: AppFormatId): readonly ApplicationQuestion[] | null {
  return PROGRAM_APPLICATIONS[formatId]?.questions ?? null;
}

export function formatApplyUrl(formatId: AppFormatId): string | null {
  return PROGRAM_APPLICATIONS[formatId]?.applyUrl ?? null;
}

const SCRIPTED_VIDEOS: readonly ScriptedVideo[] = ["team", "founder"];

/** The video Submit and the lander name, only for a format whose questions ask for one. */
export function formatScriptedVideo(formatId: AppFormatId): ScriptedVideo | null {
  const ids = new Set(formatQuestions(formatId)?.map((q) => q.id));
  return SCRIPTED_VIDEOS.find((video) => ids.has(`${video}-video`)) ?? null;
}

/** What the program's lander, hand-off and keep-working copy may promise. */
export function programOffers(formatId: AppFormatId): ProgramOffers {
  return {
    video: formatScriptedVideo(formatId),
    interviewPrep: formatHasInterviewPrep(formatId),
  };
}

const ALL_STAGES: readonly ApplicationStage[] = Object.values(APPLICATION_STAGES);
const STAGES_WITHOUT_INTERVIEW = ALL_STAGES.filter((s) => s !== APPLICATION_STAGES.INTERVIEW);

/** The format's stages, in the order an application moves through them. */
export function applicationStages(formatId: AppFormatId): readonly ApplicationStage[] {
  return formatHasInterviewPrep(formatId) ? ALL_STAGES : STAGES_WITHOUT_INTERVIEW;
}

/**
 * The stage an application opens on, and the one its closed block names: the
 * lightbox and the block both call this, so the two never disagree.
 */
export function defaultApplicationStage(
  progress: ApplicationProgress,
  stages: readonly ApplicationStage[],
): ApplicationStage {
  if (progress.submitted) return stages[stages.length - 1]!;
  if (progress.hasDraft && !progress.holdsOnDraft) return APPLICATION_STAGES.IMPROVE;
  return APPLICATION_STAGES.DRAFT;
}

/** Draft is always open (it starts the first draft); every later stage waits for one. */
export function isApplicationStageOpen(stage: ApplicationStage, progress: ApplicationWork): boolean {
  if (stage === APPLICATION_STAGES.DRAFT) return true;
  return progress.hasDraft;
}

export function nextApplicationStage(
  stages: readonly ApplicationStage[],
  current: ApplicationStage,
): ApplicationStage | null {
  const index = stages.indexOf(current);
  return index >= 0 ? (stages[index + 1] ?? null) : null;
}

type RowProgressFields = "phase" | "generating" | "lander";

/** Progress as the row reports it, before the panel has fetched its own. */
export function progressFromRow(
  application: Pick<AcceleratorRowApplication, RowProgressFields> | null,
): ApplicationProgress {
  return {
    hasDraft: application !== null && application.phase !== ACCELERATOR_PHASES.PREPARE,
    generating: application?.generating ?? false,
    submitted: application?.phase === ACCELERATOR_PHASES.WAITING_FOR_INTERVIEW,
    holdsOnDraft: application?.lander === true,
  };
}

/** The stage the closed Application block shows. */
export function applicationStageForRow(
  application: Pick<AcceleratorRowApplication, "formatId" | RowProgressFields>,
): ApplicationStage {
  return defaultApplicationStage(progressFromRow(application), applicationStages(application.formatId));
}
